import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Check, Clock, Loader2, ShoppingBag } from 'lucide-react';
import { fetchPaymentStatus, fetchOrderBySession } from '@/lib/api';
import { formatAUD } from '@/context/CartContext';
import { useCart } from '@/context/CartContext';
import { trackEvent } from '@/lib/analytics';
import Seo from '@/components/Seo';

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState(null);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [polls, setPolls] = useState(0);
  const { clear: clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) { setError('Missing session'); return; }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      tries++;
      try {
        const s = await fetchPaymentStatus(sessionId);
        if (cancelled) return;
        setStatus(s);
        setPolls(tries);
        if (s.payment_status === 'paid') {
          try {
            const o = await fetchOrderBySession(sessionId);
            if (!cancelled) setOrder(o);
            // Fire the revenue-attributed conversion event exactly once per
            // successful poll. Plausible dedupes by session so refreshes
            // won't inflate the number.
            try {
              const revenue = Number(o?.total) || Number(s?.amount_total) / 100 || 0;
              trackEvent('Purchase Completed', {
                revenue: { currency: (o?.currency || 'AUD').toUpperCase(), amount: revenue },
                items: (o?.items?.length ?? 0).toString(),
                is_subscription: o?.stripe_subscription_id ? 'yes' : 'no',
              });
            } catch { /* analytics never blocks UX */ }
          } catch { /* ignore */ }
          clearCart();
          return;
        }
        if (s.payment_status === 'failed' || s.payment_status === 'expired') return;
        if (tries < 15) {
          setTimeout(poll, 2000);
        } else if (!cancelled) {
          // Poll exhausted — Stripe hasn't finalised yet (or webhook is slow).
          setError('Still processing — Stripe usually finalises within a minute. We\'ll email you the receipt as soon as payment settles.');
        }
      } catch (e) {
        if (!cancelled) {
          setPolls(tries);
          // Prefer the friendly copy over the raw axios/HTTP message so
          // customers don't see "Request failed with status code 404".
          const isNetworkErr = !!(e && (e.response || e.request));
          setError(isNetworkErr
            ? 'We couldn\'t reach Stripe to verify your payment right now. If your card was charged, please contact us — the receipt will still land in your inbox shortly.'
            : (e?.message || 'We couldn\'t verify your payment. If your card was charged, please contact us.'));
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = status?.payment_status === 'paid';
  // Show error state when the very first poll fails (status still null) OR
  // when polling exhausts without a 'paid' resolution.
  const showError = !!error && !paid;

  return (
    <div className="section-light min-h-screen bg-[var(--mw-bg)] text-white flex flex-col">
      <Seo title="Order Confirmed" path="/order/success" noindex />
      <header className="border-b border-white/10 px-6 py-5">
        <Link to="/" className="text-[10px] uppercase tracking-[0.32em] text-white/60 hover:text-white">
          ← Back to shop
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div data-testid="order-success" className="max-w-lg w-full text-center">
          {showError ? (
            <>
              <div className="mx-auto w-20 h-20 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mb-8">
                <Clock size={30} className="text-amber-400" />
              </div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-amber-400 mb-3">Verification pending</div>
              <h1 className="font-display font-light text-3xl sm:text-4xl leading-tight tracking-tight mb-4">
                We couldn&apos;t confirm this payment yet.
              </h1>
              <p data-testid="order-success-error" className="text-white/70 leading-relaxed mb-8 max-w-sm mx-auto">
                {error}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/"
                  data-testid="order-success-back"
                  className="inline-flex items-center gap-2 border border-white/25 hover:border-white text-white text-[11px] uppercase tracking-[0.22em] px-6 py-3.5 transition-colors"
                >
                  <ShoppingBag size={14} /> Back to shop
                </Link>
                <a
                  href="mailto:trade@themeatworks.com.au"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] px-6 py-3.5 border border-white/25 hover:border-white text-white transition-colors"
                >
                  Contact support
                </a>
              </div>
            </>
          ) : !status ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 size={32} className="animate-spin text-white/60" />
              <div className="text-[11px] uppercase tracking-[0.28em] text-white/60">
                Verifying payment…
              </div>
            </div>
          ) : paid ? (
            <>
              <div className="mx-auto w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-8">
                <Check size={36} className="text-emerald-400" />
              </div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-emerald-400 mb-3">Order confirmed</div>
              <h1 className="font-display font-light text-4xl sm:text-5xl leading-tight tracking-tight mb-4">
                Thanks for your order.
              </h1>
              <p className="text-white/60 leading-relaxed mb-8 max-w-sm mx-auto">
                We&apos;ve emailed you a receipt and our trade team will be in touch shortly with your cold-chain
                delivery window across WA.
              </p>
              {order && (
                <div className="border border-white/10 bg-[var(--mw-surface)] text-left px-6 py-5 mb-8">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-3">
                    Order {order.id}
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    {(order.items || []).map((it) => (
                      <li key={it.product_id} className="flex justify-between text-white/80">
                        <span>
                          {it.name} · {it.unit === 'kg' ? `${it.quantity}kg` : `x${it.quantity}`}
                        </span>
                        <span className="tabular-nums">{formatAUD(it.line_total)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 pt-3 border-t border-white/10 flex justify-between text-sm text-white/70">
                    <span>Subtotal</span><span className="tabular-nums">{formatAUD(order.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-white/70">
                    <span>Delivery</span><span className="tabular-nums">{order.shipping === 0 ? 'FREE' : formatAUD(order.shipping)}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-white/10 flex justify-between">
                    <span className="text-[11px] uppercase tracking-[0.22em]">Total</span>
                    <span className="font-display text-xl text-[var(--mw-primary)] tabular-nums">
                      {formatAUD(order.total)} AUD
                    </span>
                  </div>
                </div>
              )}
              <Link
                to="/"
                data-testid="order-success-continue"
                className="inline-flex items-center gap-2 border border-white/25 hover:border-white text-white text-[11px] uppercase tracking-[0.22em] px-6 py-3.5 transition-colors"
              >
                <ShoppingBag size={14} /> Continue shopping
              </Link>
            </>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-[0.28em] text-white/60">
                Payment status: {status.payment_status || 'unknown'}
                {polls > 0 && polls < 15 && ' — checking again…'}
              </div>
              {error && (
                <div className="mt-4 text-[var(--mw-primary)] text-sm">{error}</div>
              )}
              <Link to="/" className="mt-8 inline-flex text-[11px] uppercase tracking-[0.22em] border border-white/25 hover:border-white px-6 py-3 transition-colors">
                Back to shop
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
